/*
  3D交互文档（顾小宇）https://apidoc.nioint.com/project/6327/interface/api/424429
  所有的消息收发都按照异步处理，因为目前同步的随后也可能会变异步
  消息有两种类型，一种是发 + 收，一种是收
  发 + 收：对应sendMessage返回promise，通过then注册回调处理返回的消息，这种模式可以通过await变成同步，方便开发
  收：对应addListener + removeListener，通过code匹配回调函数，常用于类似 2008（pin点的点击）这种消息使用
*/

import { uuid, deepMerge, injectScript, loaderResources } from '../utils'

// 统一报错前缀，生产环境方便禁止报错
const consoleError = (...arg) => {
  console.info.apply(console, ['config3D:', ...arg])
}

// 不可恢复错误状态码
const IrrecoverableErrorCodes = [102, 103, 201]
// 可恢复错误状态码
const RestoredErrorCodes = [101]
// 无res_url专用报错
const NoResUrlError = [203]

// 消息type值对应的code码 参考 https://apidoc.nioint.com/project/6327/interface/api/439142
const typeEnum = {
  // UnityEngineInited: 1001, // Unity引擎初始化完成，不建议用户调用
  Exit: 1003, // 全部退出
  GlobalReport: 1006, // 全局日志上报
  ScreenOffset: 1007, // 画面偏移
  VehicleExteriorInited: 1009, // 车辆外观是否初始化
  Enter: 2001, // 对 Exit 和 EnterVehicle 封装之后的key
  EnterVehicle: 2001, // 加载控制器车款场景
  EnterVehicleProgress: 2004, // 加载进度通知(web接口暂时没有，native因为本地资源所以有)
  FocusOnItem: 2005, // 聚焦在哪个配置项上
  PinClick: 2008, // 通知Pin点的点击
  SwitchOption: 2015, // 切换配置
  EnterInterior: 2017, // 进入内饰全景
  ExitInterior: 2019, // 退出内饰全景
  SwitchInteriorPosition: 2021, // 切换内饰全景位置
  EnterImmersion: 2031, // 进入沉浸模式
  ExitImmersion: 2033, // 退出沉浸模式
  Finish: 2041, // 配置完成
  EventRecord: 2044,
  LoadVehicle: 2101, // 加载车型所有车款
  SwitchGeneration: 2103 // 切换车款
}

const typeLabelEnum = Object.keys(typeEnum).reduce((result, key) => {
  result[typeEnum[key]] = key
  return result
}, {})

export class Config3D {
  constructor(options = {}) {
    this.options = this._getOptions(options) // 合并默认配置

    this.unityInstance = null // unity实例
    this._messageCallbacks = {} // 消息回调函数会根据request_id或type值进行存储，request_id是唯一的
  }

  // 预加载资源，提供给配置器前置页面使用，车辆3D资源的url需要传入，后期可以改成传入车辆id，自动请求资源地址
  preload(carModel) {
    // 加载unity资源
    const unityTask = loaderResources(this.options.unity)

    // 加载车模资源
    const carModelUrl = this.options.carMap?.[carModel]?.res_url
    if (!carModelUrl) {
      return unityTask
    }

    const vehicleTask = loaderResources(carModelUrl + 'resources.json').then(({ data }) => {
      return Promise.all(
        (data?.sceneBundles ?? []).map(src => loaderResources(carModelUrl + src))
      )
    })

    return Promise.all([unityTask, vehicleTask])
  }

  // 初始化配置器
  async init(container) {
    this.container = this._getContainer(container) // 容器支持多格式
    this._unityResponseMessage() // window下注册unity响应接收函数
    console.time('injectScript')
    // 这里需要添加接口请求资源map的接口 *************************************************
    await injectScript(this.options.unity.loaderUrl) // 加载unity的loader
    console.timeEnd('injectScript')
    console.time('unity resource ')
    await this._createUnityInstance() // 创建unity实例
    console.timeEnd('unity resource')
    await this._responseMessage(1001) // 等待unity引擎初始化完成
    return this
  }

  // 发送消息
  sendMessage(data) {
    const request_id = uuid() // 生成唯一id，用于匹配响应回调
    const promise = this._responseMessage(request_id) // 添加响应
    this._unitySendMessage({ ...data, request_id }) // 发送消息
    return promise
  }

  // 针对指定消息进行监听，例如2008这种只有回调的码值
  addListener(type, resolveCallback, rejectCallback) {
    type = this.getTypeCode(type)
    !this._messageCallbacks[type] && (this._messageCallbacks[type] = new Set())
    this._messageCallbacks[type].add({ resolveCallback, rejectCallback })
    return this
  }

  // 移除监听
  removeListener(type) {
    type = this.getTypeCode(type)
    if (!this._messageCallbacks[type]) return // 没有不操作
    // if (callback) return this._messageCallbacks[type].delete(callback) // 删除指定的回调函数
    delete this._messageCallbacks[type] // 删除所有回调函数
    return this
  }

  // 获取type对应的码值
  getTypeCode(type) {
    return typeof type === 'string' ? typeEnum[type] : type // 字符串转code码
  }

  // 获取type对应的指令名，便于联调时直接看日志
  getTypeLabel(type) {
    return typeof type === 'string' ? type : typeLabelEnum[type] || `Unknown(${type})`
  }

  // 创建unity实例，并赋值
  _createUnityInstance() {
    try { // 必须等待unity loader加载完成才能去创建实例，内部会再加载unity其他资源，返回一个promise对象
      return window.createUnityInstance(this.container, this.options.unity).then(infinity => this.unityInstance = infinity)
    } catch (error) {
      consoleError('unity:', error)
      return Promise.reject(error)
    }
  }

  // 响应消息，用来维护异步状态和一次性回调，配合sendMessage使用
  _responseMessage(key) {
    return new Promise((resolve, reject) => {
      // 数字类型，直接走监听回调，在这里被调用，可以利用返回的promise对象
      if (typeof key === 'number') return this.addListener(key, resolve, reject)

      this._messageCallbacks[key] = {
        resolveCallback: res => {
          delete this._messageCallbacks[key] // 删除当前回调函数
          resolve(res)
        },
        rejectCallback: res => {
          delete this._messageCallbacks[key]
          reject?.(res)
        }
      }
    })
  }

  // unity发送消息
  _unitySendMessage(data) {
    const payload = {
      ...data,
      type: this.getTypeCode(data.type)
    }
    const message = JSON.stringify(payload)
    const logInfo = {
      type: payload.type,
      typeLabel: this.getTypeLabel(payload.type),
      request_id: payload.request_id,
      payload,
      message
    }

    console.info('config3D: send unity message', logInfo)

    if (!this.unityInstance?.SendMessage) return

    try {
      this.unityInstance.SendMessage('APIManager', 'OnMessage', message)
    } catch (error) {
      consoleError('unity send message error:', {
        ...logInfo,
        error
      })
    }
  }

  // 注册unity响应函数
  _unityResponseMessage() {
    window.UnityResponseMessage = res => {
      // console.log(2, 'UnityResponseMessage:', res)
      const parsedRes = JSON.parse(res)
      const { result_code, display_msg, request_id, type,  data } = parsedRes

      const { resolveCallback, rejectCallback } = this._messageCallbacks[request_id] ?? {}
      if (result_code !== 0) {
        const mappedType = IrrecoverableErrorCodes.includes(result_code) ? 1 : RestoredErrorCodes.includes(result_code) ? 2
            : NoResUrlError.includes(result_code) ? 3
              : 0
        consoleError('unity response:', display_msg ?? result_code)
        rejectCallback?.({
          res: parsedRes,
          type: mappedType
        })
        return
      }

      // 处理用户消息
      resolveCallback?.(data)

      // 处理监听函数，例如对2008的监听
      const callbacks = this._messageCallbacks[type] || this._messageCallbacks[type - 1] // 兼容回调码 +1 的情况
      callbacks && callbacks.forEach(({ resolveCallback } = {}) => resolveCallback?.(data))
    }
  }

  // 兼容selector和dom
  _getContainer(container) {
    if (typeof container === 'string') {
      container = document.querySelector(container)
      !container && consoleError('container selector error')
    }
    return container || document.body
  }

  // 处理配置参数
  _getOptions(options = {}) {
    const defaultOptions = {
      unity: { // unity对应资源
        dataUrl: 'https://cdn-app-test.nio.com/car3d/test/2022/11/08/fdf38119-d94c-4660-a1af-783149dccd90/Build/WebGL.data.unityweb',
        frameworkUrl: 'https://cdn-app-test.nio.com/car3d/test/2022/11/08/fdf38119-d94c-4660-a1af-783149dccd90/Build/WebGL.framework.js.unityweb',
        loaderUrl: 'https://cdn-app-test.nio.com/car3d/test/2022/11/08/fdf38119-d94c-4660-a1af-783149dccd90/Build/WebGL.loader.js',
        codeUrl: 'https://cdn-app-test.nio.com/car3d/test/2022/11/08/fdf38119-d94c-4660-a1af-783149dccd90/Build/WebGL.wasm.unityweb',
        devicePixelRatio: window.devicePixelRatio ?? 1,
        webglContextAttributes: {
          preserveDrawingBuffer: true
        }
      },
      carMap: {
        ET7: {
          res_url:'https://cdn-app-test.nio.com/car3d/test/2022/11/08/a78f8cd2-e3d3-48e8-8bec-41a17bd2c0e4/Configurator/ET7/Web/'
        },
        ES8: {
          res_url:'https://cdn-app-test.nio.com/car3d/test/2022/11/08/a78f8cd2-e3d3-48e8-8bec-41a17bd2c0e4/Configurator/ES8/Web/'
        },
        ES6:{
          res_url:'https://cdn-app-test.nio.com/car3d/test/2022/11/08/a78f8cd2-e3d3-48e8-8bec-41a17bd2c0e4/Configurator/ES6/Web/'
        },
        EC6: {
          res_url:'https://cdn-app-test.nio.com/car3d/test/2022/11/08/a78f8cd2-e3d3-48e8-8bec-41a17bd2c0e4/Configurator/EC6/Web/'
        },
        ES7: {
          res_url:'https://cdn-app-test.nio.com/car3d/test/2022/11/08/a78f8cd2-e3d3-48e8-8bec-41a17bd2c0e4/Configurator/ES7/Web/'
        },
        ET5: {
          res_url:'https://cdn-app-test.nio.com/car3d/test/2022/11/08/a78f8cd2-e3d3-48e8-8bec-41a17bd2c0e4/Configurator/ET5/Web/'
        },
      }
    }
    return deepMerge(defaultOptions, options)
  }
}

// 切换车辆，需要先Exit才可以，单独提出Enter方法，简化操作，避免漏掉Exit
Config3D.prototype['Enter'] = async function(data, ...args) {
  await this.Exit()
  data.res_url = data.res_url || this.options.carMap[data.car_model].res_url // 维护车辆资源包地址
  data.generation_list=this.options.carMap[data.car_model].generation_list||[]
  return this.LoadVehicle(data, ...args) // 加载新的配置
}

// 添加其他快捷指令
Object.keys(typeEnum).forEach(key => {
  if (Config3D.prototype[key]) return // 不覆盖已经创建的命令

  Config3D.prototype[key] = function(data, ...args) {
    console.log('sendMessage', data, key, ...args)
    return this.sendMessage({ data, type: key }, ...args)
  }
})
